import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/currency';
import { SidebarNav } from '@/pages/AccountsPage';
import {
  LogOut, Menu, X, Upload, CheckCircle2, AlertCircle,
  FileText, Trash2, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BANK_PROFILES = [
  { type: 'group', label: 'Brasil' },
  { value: 'itau', label: 'Itaú' },
  { value: 'itau_card_xlsx', label: 'Itaú · factura tarjeta XLSX' },
  { value: 'santander', label: 'Santander Brasil' },
  { value: 'bb', label: 'Banco do Brasil' },
  { value: 'bradesco', label: 'Bradesco' },
  { value: 'caixa', label: 'Caixa Econômica Federal' },
  { value: 'inter_br', label: 'Banco Inter' },
  { value: 'nubank_br', label: 'Nubank Brasil' },
  { type: 'group', label: 'Colombia' },
  { value: 'itau_colombia', label: 'Itaú Colombia' },
  { value: 'bancolombia', label: 'Bancolombia' },
  { value: 'davivienda', label: 'Davivienda' },
  { value: 'banco_bogota', label: 'Banco de Bogotá' },
  { value: 'bbva_colombia', label: 'BBVA Colombia' },
  { value: 'nequi', label: 'Nequi' },
  { value: 'daviplata', label: 'Daviplata' },
  { value: 'generic_colombia', label: 'Colombia genérico' },
  { type: 'group', label: 'Perú' },
  { value: 'bcp', label: 'BCP Perú' },
  { value: 'bbva_peru', label: 'BBVA Perú' },
  { value: 'interbank', label: 'Interbank' },
  { value: 'scotiabank_peru', label: 'Scotiabank Perú' },
  { value: 'yape', label: 'Yape' },
  { value: 'plin', label: 'Plin' },
  { value: 'generic_peru', label: 'Perú genérico' },
  { type: 'group', label: 'Estados Unidos' },
  { value: 'chase', label: 'Chase' },
  { value: 'bank_of_america', label: 'Bank of America' },
  { value: 'wells_fargo', label: 'Wells Fargo' },
  { value: 'citi', label: 'Citi' },
  { value: 'capital_one', label: 'Capital One' },
  { value: 'truist', label: 'Truist' },
  { value: 'generic_us', label: 'Estados Unidos genérico' },
  { type: 'group', label: 'Otros' },
  { value: 'generic_chile', label: 'Chile genérico' },
  { value: 'generic_mexico', label: 'México genérico' },
  { value: 'generic', label: 'Genérico' },
];

const ImportPage = () => {
  const { user, logout, isAdmin, currency, locale } = useAuth();
  const navigate = useNavigate();
  const fmt = (v) => formatCurrency(v, currency, locale);
  const fileRef = useRef(null);

  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [paymentSourceAccountId, setPaymentSourceAccountId] = useState('');
  const [bankProfile, setBankProfile] = useState('itau');
  const [defaultCategory, setDefaultCategory] = useState('Otros');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({}); // index -> bool
  const [overrides, setOverrides] = useState({}); // index -> { category, type, amount, description, to_account_id }
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [invalidTransferRows, setInvalidTransferRows] = useState([]);
  const isCardInvoiceMode = bankProfile === 'itau_card_xlsx' || preview?.import_kind === 'credit_card_invoice';

  const fetchInit = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const [accRes, catRes] = await Promise.all([
      fetch(`${API_URL}/api/accounts`, { headers }).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/categories`, { headers }).then(r => r.ok ? r.json() : []),
    ]);
    setAccounts(accRes);
    setCategories(catRes);
  }, []);

  useEffect(() => { fetchInit(); }, [fetchInit]);

  useEffect(() => {
    const preventBrowserFileOpen = (event) => {
      if (event.dataTransfer?.types?.includes('Files')) {
        event.preventDefault();
      }
    };
    window.addEventListener('dragover', preventBrowserFileOpen);
    window.addEventListener('drop', preventBrowserFileOpen);
    return () => {
      window.removeEventListener('dragover', preventBrowserFileOpen);
      window.removeEventListener('drop', preventBrowserFileOpen);
    };
  }, []);

  const processFile = async (file) => {
    if (!file) return;
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('bank_profile', bankProfile);
      const res = await fetch(`${API_URL}/api/imports/itau/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Error al procesar el archivo');
        return;
      }
      setPreview(data);
      const sel = {};
      const ovr = {};
      (data.transactions || []).forEach((t, i) => {
        sel[i] = t.default_selected !== false && !t.already_imported;
        ovr[i] = {};
      });
      setSelected(sel);
      setOverrides(ovr);
    } catch (err) {
      setError('Error al subir el archivo');
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
      setDragActive(false);
    }
  };

  const handleFile = async (e) => {
    await processFile(e.target.files?.[0]);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    await processFile(e.dataTransfer.files?.[0]);
  };

  const handleDrag = (e, active) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(active);
  };

  const toggle = (i) => setSelected({ ...selected, [i]: !selected[i] });
  const toggleAll = (v) => {
    const next = {};
    (preview?.transactions || []).forEach((_, i) => { next[i] = v; });
    setSelected(next);
  };

  const setOverride = (i, key, value) => {
    setInvalidTransferRows((rows) => rows.filter((row) => row !== i));
    const nextOverride = { ...(overrides[i] || {}), [key]: value };
    if (key === 'category') nextOverride.category_source = 'user';
    setOverrides({ ...overrides, [i]: nextOverride });
  };

  const getRowType = (t, i) => overrides[i]?.type ?? t.type;
  const getRowCategory = (t, i) => {
    const rowType = getRowType(t, i);
    if (rowType === 'transfer') return 'Transferencia';
    return overrides[i]?.category ?? t.category ?? defaultCategory;
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setInvalidTransferRows([]);
    const chosen = (preview.transactions || [])
      .map((t, i) => ({ t, i }))
      .filter(({ i }) => selected[i])
      .map(({ t, i }) => ({
        ...t,
        date: t.date,
        amount: parseFloat(overrides[i]?.amount ?? t.amount),
        type: getRowType(t, i),
        category: getRowCategory(t, i),
        category_was_overridden: !!overrides[i]?.category && overrides[i]?.category_source === 'user',
        description: overrides[i]?.description ?? t.description,
        account_id: overrides[i]?.account_id ?? t.account_id ?? (accountId || null),
        to_account_id: overrides[i]?.to_account_id ?? t.to_account_id ?? null,
        import_row_index: i,
      }));

    if (chosen.length === 0) {
      setError('Selecciona al menos una transacción.');
      return;
    }
    if (isCardInvoiceMode && !accountId) {
      setError('Selecciona la tarjeta de crédito donde se importará la factura.');
      return;
    }
    const invalidTransfers = chosen.filter(t => {
      if (t.type !== 'transfer') return false;
      if (isCardInvoiceMode) return !accountId || !paymentSourceAccountId || paymentSourceAccountId === accountId;
      return !t.account_id || !t.to_account_id || t.to_account_id === t.account_id;
    });
    if (invalidTransfers.length > 0) {
      const rows = invalidTransfers.map(t => t.import_row_index);
      setInvalidTransferRows(rows);
      const labels = invalidTransfers
        .slice(0, 5)
        .map(t => `fila ${t.import_row_index + 1}: ${t.description || 'sin descripción'}`)
        .join('; ');
      setError(isCardInvoiceMode
        ? `Para importar pagos de tarjeta selecciona la tarjeta y una cuenta origen diferente. Revisa ${labels}.`
        : `Hay transferencias sin cuenta origen/destino válida. En cada transferencia selecciona origen y destino diferentes. Revisa ${labels}.`);
      return;
    }

    setConfirming(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/imports/itau/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_id: accountId || null,
          payment_source_account_id: paymentSourceAccountId || null,
          bank_profile: bankProfile,
          import_kind: preview.import_kind || 'bank_statement',
          invoice: preview.invoice || null,
          default_category: defaultCategory,
          transactions: chosen,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Error al guardar');
        return;
      }
      setResult(data);
      setPreview(null);
    } finally {
      setConfirming(false);
    }
  };

  const cancelPreview = () => {
    setPreview(null);
    setSelected({});
    setOverrides({});
    setAiMsg('');
  };

  const autoCategorize = async () => {
    if (!preview) return;
    setAiBusy(true);
    setAiMsg('');
    try {
      const token = localStorage.getItem('token');
      const body = {
        transactions: preview.transactions.map(t => ({
          description: t.description,
          type: t.type,
          amount: t.amount,
        })),
      };
      const res = await fetch(`${API_URL}/api/imports/itau/categorize`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setAiMsg(data.detail || 'No se pudo categorizar');
        return;
      }
      const next = { ...overrides };
      (data.suggestions || []).forEach(({ index, category, source }) => {
        next[index] = { ...(next[index] || {}), category, category_source: source === 'learned' ? 'learned' : 'ai' };
      });
      setOverrides(next);
      setAiMsg(`✨ ${data.count || 0} de ${preview.count} transacción(es) categorizadas con IA`);
      setTimeout(() => setAiMsg(''), 5000);
    } catch (err) {
      setAiMsg('Error al categorizar: ' + (err.message || ''));
    } finally {
      setAiBusy(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const totalSelected = preview ? Object.values(selected).filter(Boolean).length : 0;
  const expenseCats = categories.filter(c => c.type === 'expense' && c.is_active).map(c => c.name);
  const incomeCats = categories.filter(c => c.type === 'income' && c.is_active).map(c => c.name);
  const targetAccounts = isCardInvoiceMode ? accounts.filter(a => a.type === 'credit_card') : accounts;
  const paymentSourceAccounts = accounts.filter(a => a.type !== 'credit_card');

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-label="Menú">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-zinc-900 border-r border-zinc-800 p-6 z-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 p-2 -mr-2" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <SidebarNav active="import" isAdmin={isAdmin} onClick={() => setMobileMenuOpen(false)} />
            <div className="mt-8 pt-6 border-t border-zinc-800">
              <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
        </div>
        <SidebarNav active="import" isAdmin={isAdmin} />
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">Conectado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <div className="flex items-center gap-3 mb-6">
          <button className="lg:hidden text-zinc-300 p-2 -ml-2" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menú">
            <Menu className="h-6 w-6" />
          </button>
          <div className="min-w-0">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Importar extracto bancario</h2>
            <p className="text-zinc-400 text-sm">Sube un extracto CSV u OFX y escoge el banco o país del archivo</p>
          </div>
        </div>

        {result && (
          <Card className="bg-emerald-500/10 border-emerald-500/30 mb-6" data-testid="import-result">
            <CardContent className="p-5 flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-400 shrink-0" />
              <div>
                <p className="font-semibold text-emerald-300">Importación completada</p>
                <p className="text-sm text-zinc-300">
                  {result.inserted} transacción(es) insertada(s){result.skipped > 0 ? `, ${result.skipped} omitida(s)` : ''}{result.duplicates > 0 ? `, ${result.duplicates} duplicada(s)` : ''}.
                </p>
                <Button variant="outline" className="border-zinc-700 hover:bg-zinc-800 text-zinc-200 mt-3" onClick={() => setResult(null)}>
                  Importar otro archivo
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="bg-red-500/10 border-red-500/30 mb-6">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
              <p className="text-sm text-red-300">{error}</p>
            </CardContent>
          </Card>
        )}

        {!preview && !result && (
          <>
            {/* Upload area */}
            <Card className="bg-zinc-900/40 border-zinc-800 mb-6">
              <CardContent className="p-6">
                <div className={`grid grid-cols-1 gap-4 mb-4 ${isCardInvoiceMode ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}>
                  <div>
                    <Label htmlFor="imp-bank">Banco / formato</Label>
                    <Select value={bankProfile} onValueChange={setBankProfile}>
                      <SelectTrigger id="imp-bank" className="bg-zinc-800 border-zinc-700" data-testid="imp-bank-trigger">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BANK_PROFILES.map((bank) => (
                          bank.type === 'group'
                            ? <SelectItem key={bank.label} value={`group-${bank.label}`} disabled className="text-xs uppercase tracking-wide text-zinc-500">{bank.label}</SelectItem>
                            : <SelectItem key={bank.value} value={bank.value}>{bank.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="imp-account">{isCardInvoiceMode ? 'Tarjeta destino' : 'Cuenta del extracto / origen'}</Label>
                    <Select value={accountId || 'none'} onValueChange={(v) => {
                      setInvalidTransferRows([]);
                      setAccountId(v === 'none' ? '' : v);
                    }}>
                      <SelectTrigger id="imp-account" className="bg-zinc-800 border-zinc-700" data-testid="imp-account-trigger">
                        <SelectValue placeholder={isCardInvoiceMode ? 'Sin tarjeta' : 'Selecciona la cuenta del extracto'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{isCardInvoiceMode ? 'Sin tarjeta' : 'Sin cuenta del extracto'}</SelectItem>
                        {targetAccounts.map(a => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name} {a.type === 'credit_card' ? '· tarjeta' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isCardInvoiceMode && (
                    <div>
                      <Label htmlFor="imp-payment-source">Cuenta usada para pagos</Label>
                      <Select value={paymentSourceAccountId || 'none'} onValueChange={(v) => setPaymentSourceAccountId(v === 'none' ? '' : v)}>
                        <SelectTrigger id="imp-payment-source" className="bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Opcional" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No importar pagos</SelectItem>
                          {paymentSourceAccounts.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="imp-cat">Categoría por defecto</Label>
                    <Select value={defaultCategory} onValueChange={setDefaultCategory}>
                      <SelectTrigger id="imp-cat" className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...new Set([...expenseCats, ...incomeCats])].sort().map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <label
                  htmlFor="imp-file"
                  onDragEnter={(e) => handleDrag(e, true)}
                  onDragOver={(e) => handleDrag(e, true)}
                  onDragLeave={(e) => handleDrag(e, false)}
                  onDrop={handleDrop}
                  className={`block border-2 border-dashed transition-colors rounded-xl p-10 text-center cursor-pointer ${
                    dragActive
                      ? 'border-violet-400 bg-violet-500/10'
                      : 'border-zinc-700 hover:border-violet-500'
                  }`}
                  data-testid="upload-area"
                >
                  <Upload className="h-10 w-10 text-zinc-500 mx-auto mb-3" />
                  <p className="text-lg font-medium text-white mb-1">
                    {loading ? 'Procesando…' : 'Arrastra tu archivo aquí o haz clic'}
                  </p>
                  <p className="text-sm text-zinc-500">Formatos: CSV / OFX de banco o XLSX de factura cerrada Itaú</p>
                  <input
                    id="imp-file"
                    ref={fileRef}
                    type="file"
                    accept=".csv,.ofx,.qfx,.txt,.xlsx"
                    onChange={handleFile}
                    className="hidden"
                    data-testid="file-input"
                  />
                </label>
              </CardContent>
            </Card>

            {/* Help */}
            <Card className="bg-zinc-900/30 border-zinc-800">
              <CardContent className="p-5 text-sm text-zinc-400 space-y-2">
                <p className="text-white font-semibold flex items-center gap-2"><FileText className="h-4 w-4" /> ¿Dónde consigo el archivo?</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Accede a tu banca web o app del banco.</li>
                  <li>Ve a <em>Extrato</em> y elige el periodo.</li>
                  <li>Descarga el extracto como <strong>CSV</strong> u <strong>OFX</strong>, o la factura de tarjeta Itaú como <strong>XLSX</strong>.</li>
                  <li>Selecciona el banco/formato y sube el archivo aquí. Verás un preview antes de guardar.</li>
                </ol>
                <p className="pt-2 text-xs">En CSV genérico usa columnas parecidas a: Data/Fecha/Date, Descrição/Descripción/Description/Concepto y Valor/Monto/Amount. Valores negativos = gastos, positivos = ingresos.</p>
              </CardContent>
            </Card>
          </>
        )}

        {/* Preview */}
        {preview && !result && (
          <Card className="bg-zinc-900/40 border-zinc-800" data-testid="preview-card">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <p className="text-zinc-400 text-sm">Vista previa · {preview.bank_name || BANK_PROFILES.find(b => b.value === bankProfile)?.label} · formato {preview.format?.toUpperCase()}</p>
                  <p className="text-white font-semibold">
                    {totalSelected} de {preview.count} transacciones seleccionadas
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Ingresos: <span className="text-emerald-400">{fmt(preview.total_income)}</span>
                    {' · '}
                    Gastos: <span className="text-red-400">{fmt(preview.total_expense)}</span>
                    {preview.total_transfer > 0 && (
                      <>
                        {' · '}
                        Transferencias: <span className="text-sky-300">{fmt(preview.total_transfer)}</span>
                      </>
                    )}
                  </p>
                  {preview.import_kind === 'credit_card_invoice' && preview.invoice && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs">
                      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-2">
                        <p className="text-zinc-500">Factura</p>
                        <p className="text-zinc-200 font-medium">{preview.invoice.period || preview.invoice.title}</p>
                      </div>
                      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-2">
                        <p className="text-zinc-500">Vencimiento</p>
                        <p className="text-zinc-200 font-medium">{preview.invoice.due_date || '-'}</p>
                      </div>
                      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-2">
                        <p className="text-zinc-500">Total Itaú</p>
                        <p className="text-zinc-200 font-medium">{fmt(preview.invoice.invoice_total || 0)}</p>
                      </div>
                      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-2">
                        <p className="text-zinc-500">Duplicados</p>
                        <p className="text-zinc-200 font-medium">{preview.duplicate_count || 0}</p>
                      </div>
                    </div>
                  )}
                </div>
                {preview.import_kind === 'credit_card_invoice' && (
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 min-w-[260px] sm:min-w-[320px] space-y-3">
                    <div>
                      <Label htmlFor="preview-card-account">Tarjeta donde se importará la factura</Label>
                      <Select value={accountId || 'none'} onValueChange={(v) => setAccountId(v === 'none' ? '' : v)}>
                        <SelectTrigger id="preview-card-account" className="bg-zinc-800 border-zinc-700 mt-1">
                          <SelectValue placeholder="Selecciona la tarjeta" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Selecciona la tarjeta</SelectItem>
                          {accounts.filter(a => a.type === 'credit_card').map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="preview-payment-source">Cuenta usada para pagos</Label>
                      <Select value={paymentSourceAccountId || 'none'} onValueChange={(v) => setPaymentSourceAccountId(v === 'none' ? '' : v)}>
                        <SelectTrigger id="preview-payment-source" className="bg-zinc-800 border-zinc-700 mt-1">
                          <SelectValue placeholder="Opcional" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No importar pagos</SelectItem>
                          {paymentSourceAccounts.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {!accountId && (
                      <p className="text-xs text-amber-300">Selecciona la tarjeta antes de importar.</p>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    className="border-violet-500/40 bg-violet-500/5 hover:bg-violet-500/10 text-violet-200"
                    onClick={autoCategorize}
                    disabled={aiBusy}
                    data-testid="auto-categorize-btn"
                  >
                    {aiBusy
                      ? <span className="inline-flex items-center"><span className="h-3 w-3 mr-2 border-2 border-violet-300 border-t-transparent rounded-full animate-spin" /> Categorizando…</span>
                      : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Auto-categorizar con IA</>}
                  </Button>
                  <Button variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800" onClick={() => toggleAll(true)}>Todas</Button>
                  <Button variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800" onClick={() => toggleAll(false)}>Ninguna</Button>
                  <Button variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={cancelPreview}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Descartar
                  </Button>
                  <Button className="bg-violet-600 hover:bg-violet-700" onClick={handleConfirm} disabled={confirming || totalSelected === 0 || (preview.import_kind === 'credit_card_invoice' && !accountId)} data-testid="confirm-import-btn">
                    {confirming ? 'Importando…' : `Importar ${totalSelected}`}
                  </Button>
                </div>
              </div>

              {aiMsg && (
                <div className="mb-3 p-2 rounded bg-violet-500/10 border border-violet-500/30 text-violet-200 text-sm" data-testid="ai-msg">
                  {aiMsg}
                </div>
              )}

              <div className="overflow-x-auto rounded-lg border border-zinc-800">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-900/80 text-zinc-400">
                    <tr>
                      <th className="px-2 py-2 text-left w-10"></th>
                      <th className="px-2 py-2 text-left">Fecha</th>
                      <th className="px-2 py-2 text-left">Descripción</th>
                      <th className="px-2 py-2 text-left">Tipo</th>
                      <th className="px-2 py-2 text-left">Categoría</th>
                      <th className="px-2 py-2 text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.transactions.map((t, i) => {
                      const rowInvalid = invalidTransferRows.includes(i);
                      return (
                      <tr key={i} className={`border-t ${rowInvalid ? 'border-red-500/50 bg-red-500/10' : 'border-zinc-800'} ${!selected[i] ? 'opacity-40' : ''}`} data-testid={`preview-row-${i}`}>
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={!!selected[i]}
                            onChange={() => toggle(i)}
                            className="h-4 w-4 accent-violet-500"
                            aria-label="Incluir esta transacción"
                          />
                        </td>
                        <td className="px-2 py-2 text-zinc-300 whitespace-nowrap">{t.date}</td>
                        <td className="px-2 py-2 text-zinc-300 max-w-xs truncate">
                          <Input
                            value={overrides[i]?.description ?? t.description}
                            onChange={(e) => setOverride(i, 'description', e.target.value)}
                            className="h-7 text-xs bg-zinc-900 border-zinc-800 w-full"
                          />
                          <div className="mt-1 flex flex-wrap gap-1">
                            {t.already_imported && <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 text-[10px]">Duplicado</span>}
                            {t.source_kind === 'payment' && <span className="px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300 text-[10px]">Pago detectado</span>}
                            {t.is_projected_installment && <span className="px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300 text-[10px]">Cuota futura</span>}
                            {t.is_international && <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 text-[10px]">Internacional</span>}
                            {t.is_iof && <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-300 text-[10px]">IOF</span>}
                          </div>
                          {t.original_currency_info && (
                            <p className="mt-1 text-[10px] text-zinc-500">{t.original_currency_info}</p>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <Select
                            value={getRowType(t, i)}
                            onValueChange={(v) => {
                              setOverrides({
                                ...overrides,
                                [i]: {
                                  ...(overrides[i] || {}),
                                  type: v,
                                  category: v === 'transfer' ? 'Transferencia' : (overrides[i]?.category ?? t.category ?? defaultCategory),
                                }
                              });
                            }}
                          >
                            <SelectTrigger className="h-7 text-xs bg-zinc-900 border-zinc-800 w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="income">Ingreso</SelectItem>
                              <SelectItem value="expense">Gasto</SelectItem>
                              <SelectItem value="transfer">Transferencia</SelectItem>
                            </SelectContent>
                          </Select>
                          {t.suggestion_confidence && t.suggested_type === 'transfer' && (
                            <p className="mt-1 text-[10px] text-sky-300">{t.suggestion_reason}</p>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {getRowType(t, i) === 'transfer' ? (
                            <div className="space-y-1">
                              <div className="h-7 inline-flex items-center px-2 rounded-md border border-zinc-800 bg-zinc-900 text-xs text-sky-300">
                                Transferencia
                              </div>
                              {rowInvalid && (
                                <p className="text-[10px] text-red-300">Selecciona origen y destino diferentes</p>
                              )}
                              {preview.import_kind === 'credit_card_invoice' ? (
                                <p className="text-[10px] text-zinc-500">Usa la cuenta origen configurada arriba</p>
                              ) : (
                                <div className="grid grid-cols-1 gap-1">
                                  <Select
                                    value={overrides[i]?.account_id ?? t.account_id ?? accountId ?? 'none'}
                                    onValueChange={(v) => setOverride(i, 'account_id', v === 'none' ? '' : v)}
                                  >
                                    <SelectTrigger className="h-7 text-xs bg-zinc-900 border-zinc-800 w-44">
                                      <SelectValue placeholder="Cuenta origen" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">Cuenta origen</SelectItem>
                                      {accounts.map(a => (
                                        <SelectItem key={a.id} value={a.id}>
                                          {a.name}{a.type === 'credit_card' ? ' · tarjeta' : a.type === 'investment' ? ' · inversiones' : ''}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Select
                                    value={overrides[i]?.to_account_id ?? t.to_account_id ?? 'none'}
                                    onValueChange={(v) => setOverride(i, 'to_account_id', v === 'none' ? '' : v)}
                                  >
                                    <SelectTrigger className="h-7 text-xs bg-zinc-900 border-zinc-800 w-44">
                                      <SelectValue placeholder="Cuenta destino" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">Cuenta destino</SelectItem>
                                      {accounts.map(a => (
                                        <SelectItem key={a.id} value={a.id}>
                                          {a.name}{a.type === 'credit_card' ? ' · tarjeta' : a.type === 'investment' ? ' · inversiones' : ''}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>
                          ) : (
                            <Select
                              value={getRowCategory(t, i)}
                              onValueChange={(v) => setOverride(i, 'category', v)}
                            >
                              <SelectTrigger className="h-7 text-xs bg-zinc-900 border-zinc-800 w-36">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(getRowType(t, i) === 'expense' ? expenseCats : incomeCats).map(c => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className={`px-2 py-2 text-right font-semibold whitespace-nowrap ${getRowType(t, i) === 'income' ? 'text-emerald-400' : getRowType(t, i) === 'transfer' ? 'text-sky-300' : 'text-red-400'}`}>
                          {getRowType(t, i) === 'income' ? '+' : getRowType(t, i) === 'expense' ? '-' : ''}{fmt(t.amount)}
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ImportPage;
